import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import '../styles/FarmAIChat.css'
import { backendService } from '../../../services/backendService'
import type { AssistantChatResponse, AssistantChatTurn, ConversationSummary } from '../../../services/api/assistantApi'

interface FarmAIChatProps {
  // Unified AI assistant — no props needed; topic is always 'girasoles_assistant'
}

interface DisplayMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  pending?: boolean
  tokensUsed?: number
  modelUsed?: string
  aiTokenBalance?: number | null
  thinking?: string | null
}

// Cost display per model — mirrors backend token_costs.py
const MODEL_COST_LABELS: Record<string, string> = {
  'deepseek-v4-flash': '1 token/word',
  'deepseek-r1:1.5b': '1 token/word',
  'deepseek-r1:7b': '1 token/word',
  'deepseek-r1:32b': '10 tokens/word',
  'deepseek-r1:70b': '10 tokens/word',
}
function getModelCostLabel(model: string): string {
  if (MODEL_COST_LABELS[model]) return MODEL_COST_LABELS[model]
  if (model.toLowerCase().includes('deepseek')) return '1 token/word'
  return 'Free (local)'
}
function isPaidModel(model: string): boolean {
  return model.toLowerCase().includes('deepseek')
}

const EMPTY_STATE_COPY = {
  heading: 'Girasoles AI Assistant',
  body: 'Ask about Girasoles Toolbox, materials, print debugging, or any other topic — the assistant has knowledge across all areas.',
}

const kind = 'girasoles_assistant' as const

export const FarmAIChat: React.FC<FarmAIChatProps> = () => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, DisplayMessage[]>>({})
  const [activeId, setActiveId] = useState<string | null>(null)
  const [loadingConvos, setLoadingConvos] = useState(true)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const [input, setInput] = useState('')

  // Model selector
  const [availableModels, setAvailableModels] = useState<string[]>([])
  const [selectedModel, setSelectedModel] = useState<string>('')


  const transcriptRef = useRef<HTMLDivElement | null>(null)
  const renameInputRef = useRef<HTMLInputElement | null>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages = activeId ? (messagesByConversation[activeId] ?? []) : []

  // ── Load conversations from backend ─────────────────────────────────────────

  const loadConversations = useCallback(async () => {
    try {
      const convos = await backendService.listConversations(kind)
      setConversations(convos)
      if (convos.length > 0) {
        setActiveId(id => id ?? convos[0].id)
      }
    } catch {
      // Farm may not be logged in or backend down — silently degrade to in-memory
    } finally {
      setLoadingConvos(false)
    }
  }, [])

  useEffect(() => {
    void loadConversations()
    backendService.getAssistantHealth().then(h => {
      const models = h.available_models ?? []
      setAvailableModels(models)
      if (models.length > 0) setSelectedModel(models[0])
    }).catch(() => {/* ignore */})
  }, [loadConversations])

  // ── Load messages when switching to a conversation ───────────────────────────

  useEffect(() => {
    if (!activeId) return
    if (messagesByConversation[activeId]) return // already loaded
    if (activeId.startsWith('local-')) return   // temp local conversation

    backendService.getConversationMessages(activeId).then(msgs => {
      const display: DisplayMessage[] = msgs.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        tokensUsed: m.tokens_used ?? 0,
        modelUsed: m.model_used ?? undefined,
      }))
      setMessagesByConversation(prev => ({ ...prev, [activeId]: display }))
    }).catch(() => {/* ignore */})
  }, [activeId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const node = transcriptRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [messages])

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [renamingId])

  // ── Conversation management ──────────────────────────────────────────────────

  const handleNewChat = async () => {
    try {
      const convo = await backendService.createConversation(kind)
      setConversations(prev => [convo, ...prev])
      setMessagesByConversation(prev => ({ ...prev, [convo.id]: [] }))
      setActiveId(convo.id)
    } catch {
      // Fallback: local-only conversation if backend unavailable
      const id = `local-${kind}-${Date.now()}`
      const fallback: ConversationSummary = {
        id, farm_id: '', kind, title: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_message_preview: null,
      }
      setConversations(prev => [fallback, ...prev])
      setMessagesByConversation(prev => ({ ...prev, [id]: [] }))
      setActiveId(id)
    }
  }

  const handleDelete = async (id: string) => {
    if (!id.startsWith('local-')) {
      try { await backendService.deleteConversation(id) } catch {/* ignore */}
    }
    setConversations(prev => prev.filter(c => c.id !== id))
    setMessagesByConversation(prev => { const n = { ...prev }; delete n[id]; return n })
    if (activeId === id) {
      setActiveId(conversations.find(c => c.id !== id)?.id ?? null)
    }
  }

  const startRename = (id: string, currentTitle: string | null) => {
    setRenamingId(id)
    setRenameValue(currentTitle ?? '')
  }

  const commitRename = async (id: string) => {
    const title = renameValue.trim() || 'Untitled chat'
    setRenamingId(null)
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c))
    if (!id.startsWith('local-')) {
      try { await backendService.renameConversation(id, title) } catch {/* ignore */}
    }
  }

  // ── Send ─────────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed) return

    let conversationId = activeId
    if (!conversationId) {
      try {
        const convo = await backendService.createConversation(kind, trimmed.slice(0, 60) || undefined)
        setConversations(prev => [convo, ...prev])
        setMessagesByConversation(prev => ({ ...prev, [convo.id]: [] }))
        setActiveId(convo.id)
        conversationId = convo.id
      } catch {
        conversationId = `local-${kind}-${Date.now()}`
        const fallback: ConversationSummary = {
          id: conversationId, farm_id: '', kind, title: trimmed.slice(0, 60) || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_message_preview: null,
        }
        setConversations(prev => [fallback, ...prev])
        setMessagesByConversation(prev => ({ ...prev, [conversationId!]: [] }))
        setActiveId(conversationId)
      }
    }

    const convId = conversationId!
    const userMsgId = `msg-u-${Date.now()}`
    const assistantMsgId = `msg-a-${Date.now()}`
    const userContent = trimmed

    const priorMessages = messagesByConversation[convId] ?? []
    const history: AssistantChatTurn[] = priorMessages
      .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.pending && m.content)
      .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }))
    const turns: AssistantChatTurn[] = [...history, { role: 'user', content: userContent }]

    setMessagesByConversation(prev => ({
      ...prev,
      [convId]: [
        ...(prev[convId] ?? []),
        { id: userMsgId, role: 'user', content: userContent },
        { id: assistantMsgId, role: 'assistant', content: '…', pending: true },
      ],
    }))
    setConversations(prev => prev.map(c =>
      c.id === convId
        ? { ...c, updated_at: new Date().toISOString(), last_message_preview: userContent.slice(0, 80) }
        : c
    ))
    setInput('')

    try {
      const resp: AssistantChatResponse = await backendService.sendAssistantChat(kind, turns, {
        model: selectedModel || undefined,
        conversation_id: convId.startsWith('local-') ? undefined : convId,
      })
      const sources = resp.sources?.length
        ? `\n\n_Sources: ${resp.sources.map((s, i) => `[${i + 1}] ${s.heading || s.source}`).join(', ')}_`
        : ''
      const reply = (resp.answer || '').trim() + sources
      const tokensUsed = resp.tokens_used ?? 0
      const modelUsed = resp.model
      const aiTokenBalance = resp.ai_token_balance
      setMessagesByConversation(prev => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map(m =>
          m.id === assistantMsgId
            ? { ...m, content: reply || '(empty reply)', pending: false, tokensUsed, modelUsed, aiTokenBalance }
            : m
        ),
      }))
      setConversations(prev => prev.map(c =>
        c.id === convId
          ? { ...c, updated_at: new Date().toISOString(), last_message_preview: (resp.answer || '').slice(0, 80) }
          : c
      ))
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setMessagesByConversation(prev => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map(m =>
          m.id === assistantMsgId ? { ...m, content: `Error: ${msg}`, pending: false } : m
        ),
      }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const costLabel = useMemo(() => getModelCostLabel(selectedModel), [selectedModel])
  const copy = EMPTY_STATE_COPY

  return (
    <div className="farm-ai-chat" data-testid="farm-ai-chat-girasoles_assistant">
      <aside className="farm-ai-chat__sidebar">
        <div className="farm-ai-chat__sidebar-header">
          <h3>Conversations</h3>
          <button
            type="button"
            className="farm-ai-chat__new-btn"
            onClick={() => void handleNewChat()}
            data-testid="farm-ai-chat-new"
          >
            + New
          </button>
        </div>

        {loadingConvos ? (
          <p className="farm-ai-chat__convo-list-empty">Loading…</p>
        ) : conversations.length === 0 ? (
          <p className="farm-ai-chat__convo-list-empty">No chats yet — start a new one.</p>
        ) : (
          <ul className="farm-ai-chat__convo-list">
            {conversations.map(c => (
              <li
                key={c.id}
                className={c.id === activeId ? 'is-active' : ''}
                onClick={() => { if (renamingId !== c.id) setActiveId(c.id) }}
                data-testid="farm-ai-chat-convo-item"
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renamingId === c.id ? (
                    <input
                      ref={renameInputRef}
                      className="farm-ai-chat__rename-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => void commitRename(c.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void commitRename(c.id)
                        if (e.key === 'Escape') setRenamingId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                      data-testid="farm-ai-chat-rename-input"
                    />
                  ) : (
                    <div
                      className="farm-ai-chat__convo-title"
                      onDoubleClick={e => { e.stopPropagation(); startRename(c.id, c.title) }}
                      title="Double-click to rename"
                    >
                      {c.title || 'Untitled chat'}
                    </div>
                  )}
                  {c.last_message_preview && (
                    <div className="farm-ai-chat__convo-preview">{c.last_message_preview}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="farm-ai-chat__convo-delete"
                  aria-label="Delete conversation"
                  data-testid="farm-ai-chat-convo-delete"
                  onClick={e => { e.stopPropagation(); void handleDelete(c.id) }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="farm-ai-chat__main">
        {/* Model selector */}
        {availableModels.length > 0 && (
          <div className="farm-ai-chat__model-bar">
            <label htmlFor="model-select-girasoles_assistant" className="farm-ai-chat__model-label">Model:</label>
            <select
              id="model-select-girasoles_assistant"
              className="farm-ai-chat__model-select"
              value={selectedModel}
              onChange={e => setSelectedModel(e.target.value)}
              data-testid="farm-ai-chat-model-select"
            >
              {availableModels.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <span className="farm-ai-chat__model-cost" data-testid="farm-ai-chat-model-cost">{costLabel}</span>
          </div>
        )}

        <div className="farm-ai-chat__transcript" ref={transcriptRef} data-testid="farm-ai-chat-transcript">
          {messages.length === 0 ? (
            <div className="farm-ai-chat__empty">
              <h3 style={{ margin: '0 0 8px 0' }}>{copy.heading}</h3>
              <p>{copy.body}</p>
            </div>
          ) : (
            messages.map(m => (
              <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div
                  className={`farm-ai-chat__bubble ${m.role === 'user' ? 'is-user' : 'is-assistant'}`}
                  data-testid={m.role === 'user' ? 'farm-ai-chat-user-bubble' : 'farm-ai-chat-assistant-bubble'}
                >
                  {m.content && <div>{m.content}</div>}
                  {m.pending && !m.content && <span style={{ color: '#94a3b8' }}>…</span>}
                  {m.role === 'assistant' && !m.pending && m.content && (
                    <>
                      {m.thinking && (
                        <details
                          className="farm-ai-chat__thinking"
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            fontSize: '0.75rem',
                            color: '#475569',
                          }}
                        >
                          <summary
                            style={{
                              cursor: 'pointer',
                              fontWeight: 600,
                              color: '#64748b',
                              userSelect: 'none',
                            }}
                          >
                            Thinking…
                          </summary>
                          <div
                            style={{
                              marginTop: 8,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              lineHeight: 1.5,
                            }}
                          >
                            {m.thinking}
                          </div>
                        </details>
                      )}
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: '0.7rem',
                          color: '#64748b',
                          borderTop: '1px solid #e2e8f0',
                          paddingTop: 4,
                        }}
                      >
                        <span>~{m.content.split(/\s+/).filter(Boolean).length} words</span>
                        <span style={{ marginLeft: 10 }}>
                          {m.modelUsed && isPaidModel(m.modelUsed)
                            ? `${getModelCostLabel(m.modelUsed)}`
                            : (m.tokensUsed && m.tokensUsed > 0
                              ? `${m.tokensUsed} tokens used`
                              : 'Free model')}
                        </span>
                        {m.modelUsed && (
                          <span style={{ marginLeft: 10, opacity: 0.6 }}>({m.modelUsed})</span>
                        )}
                        {m.aiTokenBalance != null && (
                          <span style={{ marginLeft: 10, color: '#0891b2' }}>
                            {m.aiTokenBalance.toLocaleString()} tokens left
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="farm-ai-chat__composer">
          <div className="farm-ai-chat__input-row">
            <textarea
              className="farm-ai-chat__textarea"
              placeholder="Ask the AI assistant…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="farm-ai-chat-input"
            />
            <button
              type="button"
              className="farm-ai-chat__send"
              onClick={() => void handleSend()}
              disabled={!input.trim()}
              data-testid="farm-ai-chat-send"
            >
              Send
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FarmAIChat
